import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { DataTable } from "@/components/tables/DataTable";
import { useCreatePromo, usePromosList, useUpdatePromo } from "@/features/promos/usePromos";
import { toast } from "@/store/toast.store";
import { getApiErrorMessage } from "@/api/client";
import { formatDate, formatINR, formatNumber } from "@/utils/format";
import type { Promo, PromoStatus } from "@/types/domain";

const STATUS_TONE: Record<PromoStatus, "teal" | "gold" | "neutral"> = {
  ACTIVE: "teal",
  SCHEDULED: "gold",
  EXPIRED: "neutral",
};

const schema = z
  .object({
    code: z.string().min(3, "At least 3 characters").max(20).toUpperCase(),
    title: z.string().min(3, "Title is required").max(80),
    detail: z.string().max(200).optional(),
    discountType: z.enum(["PERCENT", "FLAT"]),
    value: z.coerce.number().min(1, "Must be at least 1"),
    maxDiscount: z.coerce.number().min(0).optional(),
    minFare: z.coerce.number().min(0).optional(),
    status: z.enum(["ACTIVE", "SCHEDULED", "EXPIRED"]),
    featured: z.boolean(),
    firstRideOnly: z.boolean(),
    startsAt: z.string().optional(),
    endsAt: z.string().optional(),
  })
  .refine((v) => v.discountType !== "PERCENT" || v.value <= 100, {
    message: "A percent discount can't exceed 100",
    path: ["value"],
  });
type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  code: "",
  title: "",
  detail: "",
  discountType: "FLAT",
  value: 50,
  maxDiscount: undefined,
  minFare: 0,
  status: "ACTIVE",
  featured: false,
  firstRideOnly: false,
  startsAt: "",
  endsAt: "",
};

export default function Promos() {
  const promos = usePromosList();
  const createPromo = useCreatePromo();
  const updatePromo = useUpdatePromo();

  const [editing, setEditing] = useState<Promo | "new" | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });
  const discountType = watch("discountType");

  function openCreate() {
    reset(EMPTY_FORM);
    setEditing("new");
  }

  function openEdit(promo: Promo) {
    reset({
      code: promo.code,
      title: promo.title,
      detail: promo.detail ?? "",
      discountType: promo.discountType,
      value: promo.value,
      maxDiscount: promo.maxDiscount,
      minFare: promo.minFare,
      status: promo.status,
      featured: promo.featured,
      firstRideOnly: promo.firstRideOnly,
      startsAt: promo.startsAt ? promo.startsAt.slice(0, 10) : "",
      endsAt: promo.endsAt ? promo.endsAt.slice(0, 10) : "",
    });
    setEditing(promo);
  }

  function onSubmit(values: FormValues) {
    const isNew = editing === "new";
    const payload = {
      ...values,
      detail: values.detail || undefined,
      startsAt: values.startsAt || undefined,
      endsAt: values.endsAt || undefined,
      // An empty "Max discount" field coerces to 0, not undefined — the
      // backend requires it be >= 1 when present at all, so 0 must be
      // treated the same as "not set" rather than sent as a real value.
      maxDiscount: values.maxDiscount || undefined,
    };
    const mutation = isNew ? createPromo : updatePromo;
    const body = isNew ? payload : { id: (editing as Promo).id, ...payload };
    mutation.mutate(body as never, {
      onSuccess: () => {
        toast.success(isNew ? "Promo created" : "Promo updated");
        setEditing(null);
      },
      onError: (err) => toast.error(getApiErrorMessage(err)),
    });
  }

  const columns = useMemo<ColumnDef<Promo, unknown>[]>(
    () => [
      {
        header: "Code",
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-semibold text-ink">{row.original.code}</span>
            {row.original.featured && <Badge tone="gold">Featured</Badge>}
          </div>
        ),
      },
      { header: "Title", accessorKey: "title" },
      {
        header: "Discount",
        cell: ({ row }) => {
          const p = row.original;
          const amount = p.discountType === "PERCENT" ? `${p.value}%` : formatINR(p.value);
          return (
            <span>
              {amount}
              {p.maxDiscount ? ` (cap ${formatINR(p.maxDiscount)})` : ""}
            </span>
          );
        },
      },
      {
        header: "Status",
        cell: ({ row }) => <Badge tone={STATUS_TONE[row.original.status]}>{row.original.status}</Badge>,
      },
      {
        header: "Validity",
        cell: ({ row }) => {
          const p = row.original;
          if (!p.startsAt && !p.endsAt) return "Always";
          return `${p.startsAt ? formatDate(p.startsAt) : "—"} – ${p.endsAt ? formatDate(p.endsAt) : "—"}`;
        },
      },
      {
        header: "Used",
        cell: ({ row }) => (
          <span>
            {formatNumber(row.original.usedCount)} · {formatINR(row.original.budgetSpent)}
          </span>
        ),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <button
            className="rounded-md p-1.5 text-ink-faint hover:bg-cream-200 hover:text-ink"
            title="Edit"
            onClick={() => openEdit(row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Promos & Coupons"
      description="Discount codes riders can apply at booking."
      actions={
        <Button size="sm" onClick={openCreate}>
          New promo
        </Button>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={promos.data ?? []}
          isLoading={promos.isLoading}
          error={promos.error}
          onRetry={() => promos.refetch()}
          rowKey={(p) => p.id}
          emptyTitle="No promos yet"
        />
      </div>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "New promo" : `Edit ${(editing as Promo)?.code ?? "promo"}`}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              form="promo-form"
              type="submit"
              variant="primary"
              loading={createPromo.isPending || updatePromo.isPending}
            >
              {editing === "new" ? "Create" : "Save changes"}
            </Button>
          </>
        }
      >
        <form id="promo-form" className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Code" error={errors.code?.message}>
              <Input placeholder="WELCOME50" error={!!errors.code} {...register("code")} />
            </FormField>
            <FormField label="Status" error={errors.status?.message}>
              <Select {...register("status")}>
                <option value="ACTIVE">Active</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="EXPIRED">Expired</option>
              </Select>
            </FormField>
          </div>

          <FormField label="Title" error={errors.title?.message}>
            <Input placeholder="₹50 off your first ride" error={!!errors.title} {...register("title")} />
          </FormField>

          <FormField label="Detail (shown to riders)" error={errors.detail?.message}>
            <Textarea rows={2} {...register("detail")} />
          </FormField>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Discount type" error={errors.discountType?.message}>
              <Select {...register("discountType")}>
                <option value="FLAT">Flat (₹)</option>
                <option value="PERCENT">Percent (%)</option>
              </Select>
            </FormField>
            <FormField label={discountType === "PERCENT" ? "Value (%)" : "Value (₹)"} error={errors.value?.message}>
              <Input type="number" error={!!errors.value} {...register("value")} />
            </FormField>
            <FormField label="Max discount (₹, optional)" error={errors.maxDiscount?.message}>
              <Input type="number" {...register("maxDiscount")} />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Min fare (₹)" error={errors.minFare?.message}>
              <Input type="number" {...register("minFare")} />
            </FormField>
            <FormField label="Starts" error={errors.startsAt?.message}>
              <Input type="date" {...register("startsAt")} />
            </FormField>
            <FormField label="Ends" error={errors.endsAt?.message}>
              <Input type="date" {...register("endsAt")} />
            </FormField>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" className="h-4 w-4 accent-teal-500" {...register("featured")} />
              Featured (shown in the offers list)
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" className="h-4 w-4 accent-teal-500" {...register("firstRideOnly")} />
              First ride only
            </label>
          </div>
        </form>
      </Modal>
    </PageContainer>
  );
}
